/**
 * Product data – Numbering, Recommend Age, Pieces, Block Size, Material.
 * Optional: scale. Categories: military (with subcategory + faction) | civilian.
 * Brand buckets (like quanguanbricks QuanGuan / ThirdParty): 12GO | Brixago.
 * Users check availability at partners; we do not display or guarantee any offer.
 */
import { PRODUCT_COPY } from "./productCopy";
import type { SeoProductCategory } from "./seoProductCategory";

export type ProductCategory = "military" | "civilian";
/** Podział kategorii Wojskowa: czołgi/pojazdy, okręty, inne */
export type MilitarySubcategory = "tanks_vehicles" | "ships" | "other";
export type MilitaryFaction = "germany" | "usa" | "uk" | "ussr" | "china" | "sweden";
/** Catalog manufacturer bucket — filterable like QuanGuan / ThirdParty on quanguanbricks. */
export type ProductBrand = "12GO" | "Brixago";

export const PRODUCT_BRANDS: ProductBrand[] = ["12GO", "Brixago"];
export const DEFAULT_PRODUCT_BRAND: ProductBrand = "12GO";

export function isProductBrand(value: unknown): value is ProductBrand {
  return value === "12GO" || value === "Brixago";
}

/** Parse `?brands=12GO,Brixago` query value. */
export function parseBrandsParam(raw: unknown): ProductBrand[] | undefined {
  if (typeof raw !== "string" || !raw.trim()) return undefined;
  const list = raw
    .split(",")
    .map((s) => s.trim())
    .filter(isProductBrand);
  return list.length > 0 ? list : undefined;
}

export type Product = {
  id: string;
  numbering: string;
  name?: string;
  recommendAge: string;
  pieces: number;
  blockSize: string;
  /** Finished model dimensions, e.g. "32.3 × 14 × 12.5 cm". Falls back to blockSize when not "normal". */
  productSize?: string;
  /** Outer package dimensions, e.g. "42 × 28.5 × 11.5 cm" */
  packageSize?: string;
  material: string;
  scale?: string;
  /** Defaults to 12GO when omitted in source data. */
  brand: ProductBrand;
  category: ProductCategory;
  /** Tylko dla category === "military": Czołgi i pojazdy | Okręty | Inne */
  militarySubcategory?: MilitarySubcategory;
  faction?: MilitaryFaction;
  /** SEO keyword type for title/meta (tank, tankDestroyer, …). Inferred when omitted. */
  seoCategory?: SeoProductCategory;
  /** Opis zestawu – wyświetlany w sekcji "About this set". Może być obiektem { pl: "...", en: "..." } dla wielu języków. */
  about?: string | Record<string, string>;
  /** Tło historyczne – opcjonalne. Może być obiektem { pl: "...", en: "..." } dla wielu języków. */
  historicalBackground?: string | Record<string, string>;
};

type ProductInput = Omit<Product, "brand"> & { brand?: ProductBrand };

const productsBase: ProductInput[] = [
  // Strona 1
  {
    id: "97017",
    scale: "1:28",
    numbering: "97017",
    name: "Waffentrager E-100",
    recommendAge: "14+",
    pieces: 2036,
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "tanks_vehicles",
    faction: "germany",
    about: {
      en: "The E100WT Weapon Transport Vehicle set recreates a German heavy artillery carrier from World War II. With over 2000 pieces, this detailed model features authentic design and compatible bricks for smooth assembly.",
      pl: "Zestaw E100WT Weapon Transport Vehicle odtwarza niemiecki ciężki pojazd transportowy artylerii z czasów II wojny światowej. Ponad 2000 elementów, precyzyjny model z autentycznym wzornictwem.",
    },
    historicalBackground: {
      en: "The E-100 was a German super-heavy tank design developed in 1944. The Weapon Transport variant was conceived to carry heavy artillery pieces. The project was never completed due to the end of the war.",
      pl: "E-100 był niemieckim projektem superciężkiego czołgu z 1944 roku. Wariant transportowy miał przewozić ciężkie działa. Projekt nigdy nie został ukończony ze względu na koniec wojny.",
    },
  },
  {
    id: "97026",
    scale: "1:28",
    numbering: "97026",
    name: "Karl Gerat",
    recommendAge: "6+",
    pieces: 4706,
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "tanks_vehicles",
    faction: "germany",
    about: {
      en: "The KARL GERAT set recreates the German super-heavy self-propelled siege mortar from World War II. With nearly 4700 pieces, this impressive model features the massive 60 cm caliber mortar and tracked chassis in authentic detail.",
      pl: "Zestaw KARL GERAT odtwarza niemiecki superciężki moździerz samobieżny z II wojny światowej. Prawie 4700 elementów, imponujący model z charakterystycznym moździerzem kalibru 60 cm.",
    },
    historicalBackground: {
      en: "Karl-Gerät was a German self-propelled siege mortar. Seven were built between 1940–1942. They were used in the siege of Sevastopol, Warsaw Uprising, and on the Western Front. The 60 cm shells could penetrate up to 2.5 m of reinforced concrete.",
      pl: "Karl-Gerät to niemiecki samobieżny moździerz oblężniczy. Zbudowano 7 egzemplarzy w latach 1940–1942. Użyte pod Sewastopolem, w powstaniu warszawskim i na froncie zachodnim. Pociski 60 cm przebijały do 2,5 m żelbetu.",
    },
  },
  {
    id: "97027",
    scale: "1:28",
    numbering: "97027",
    name: "Tiger II",
    recommendAge: "6+",
    pieces: 1780,
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "tanks_vehicles",
    faction: "germany",
    about: {
      en: "The Tiger II set recreates the German heavy tank also known as the King Tiger. This detailed brick-built model focuses on the iconic turret shape and armored hull proportions.",
      pl: "Zestaw Tiger II odtwarza niemiecki czołg ciężki znany jako King Tiger. Szczegółowy model z klocków skupia się na charakterystycznej wieży i proporcjach opancerzonego kadłuba.",
      de: "Das Tiger-II-Set stellt den deutschen schweren Panzer nach, der auch als „Königstiger“ bekannt ist. Das detailreiche Modell legt den Fokus auf die ikonische Turmform und die Proportionen der gepanzerten Wanne.",
      es: "El set Tiger II recrea el tanque pesado alemán, también conocido como «King Tiger». Este modelo detallado pone el foco en la icónica forma de la torreta y en las proporciones del casco blindado.",
      zh: "Tiger II 套装还原了德国重型坦克，也被称为“虎王”。该模型细节丰富，重点呈现标志性的炮塔造型与装甲车体比例。",
      fr: "Le set Tiger II recrée le char lourd allemand également connu sous le nom de « King Tiger ». Ce modèle détaillé met l’accent sur la forme emblématique de la tourelle et les proportions du blindage.",
      nl: "De Tiger II-set beeldt de Duitse zware tank uit, ook bekend als de « King Tiger ». Dit gedetailleerde model legt de nadruk op de iconische torenvorm en de proporties van de gepantserde romp.",
    },
    historicalBackground: {
      en: "The Tiger II entered service in 1944. It combined very thick frontal armor with the powerful 8.8 cm KwK 43 gun. Despite its battlefield strength, it suffered from reliability and logistics challenges late in the war.",
      pl: "Tiger II wszedł do służby w 1944 r. Łączył bardzo gruby pancerz czołowy z potężną armatą 8.8 cm KwK 43. Mimo dużych możliwości bojowych cierpiał na problemy niezawodności i logistyki pod koniec wojny.",
      de: "Der Tiger II kam 1944 in den Einsatz. Er vereinte sehr starke Frontpanzerung mit der leistungsstarken 8,8‑cm‑KwK‑43‑Kanone. Trotz seiner Stärke auf dem Schlachtfeld litt er gegen Kriegsende unter Zuverlässigkeitsproblemen und logistischen Herausforderungen.",
      es: "El Tiger II entró en servicio en 1944. Combinaba un blindaje frontal muy grueso con el potente cañón KwK 43 de 8,8 cm. Pese a su fuerza en combate, hacia el final de la guerra sufrió problemas de fiabilidad y dificultades logísticas.",
      zh: "Tiger II 于 1944 年投入使用，拥有极厚的正面装甲并配备强大的 8.8 cm KwK 43 火炮。尽管战斗力突出，但在战争后期也面临可靠性与后勤保障方面的挑战。",
      fr: "Le Tiger II est entré en service en 1944. Il associait un blindage frontal très épais au puissant canon de 8,8 cm KwK 43. Malgré sa force au combat, il a souffert de problèmes de fiabilité et de contraintes logistiques en fin de guerre.",
      nl: "De Tiger II kwam in 1944 in dienst. Hij combineerde zeer dik frontpantser met het krachtige 8,8 cm KwK 43-kanon. Ondanks zijn slagkracht had hij laat in de oorlog te kampen met betrouwbaarheidsproblemen en logistieke uitdagingen.",
    },
  },
  {
    id: "97018",
    scale: "1:28",
    numbering: "97018",
    name: "Jagdpanzer E-100",
    recommendAge: "14+",
    pieces: 1836,
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "tanks_vehicles",
    faction: "germany",
    about: {
      en: "The Jagdpanzer E-100 set models the German tank destroyer design based on the E-100 chassis. Over 1800 pieces create a detailed, heavily armored casemate vehicle with a powerful 17 cm gun.",
      pl: "Zestaw Jagdpanzer E-100 przedstawia niemiecki niszczyciel czołgów oparty na podwoziu E-100. Ponad 1800 elementów buduje szczegółowy, ciężko opancerzony pojazd z działem 17 cm.",
    },
    historicalBackground: {
      en: "Jagdpanzer E-100 was a planned German tank destroyer mounting a 17 cm gun on the E-100 super-heavy tank chassis. Only a hull prototype existed by the end of the war. The design combined exceptional firepower with heavy armor.",
      pl: "Jagdpanzer E-100 to projektowany niemiecki niszczyciel czołgów z działem 17 cm na podwoziu E-100. Do końca wojny istniał jedynie prototyp kadłuba. Projekt łączył ogromną siłę ognia z ciężkim pancerzem.",
    },
  },
  {
    id: "96012",
    scale: "Other",
    numbering: "96012",
    name: "German Leopard 2A8 Main Battle Tank",
    recommendAge: "6+",
    pieces: 2300,
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "tanks_vehicles",
    faction: "germany",
    about: {
      en: "The Leopard 2A8 set recreates Germany's latest main battle tank variant. Over 2300 pieces build a detailed model with the upgraded turret, enhanced protection, and modern optics characteristic of this NATO flagship.",
      pl: "Zestaw Leopard 2A8 odtwarza najnowszą niemiecką wersję czołgu podstawowego. Ponad 2300 elementów buduje szczegółowy model z zmodernizowaną wieżą i ulepszonym opancerzeniem.",
    },
    historicalBackground: {
      en: "The Leopard 2A8 is the latest evolution of the Leopard 2 family, developed in response to modern battlefield threats. It features improved armor, Trophy active protection system, and enhanced firepower. Ordered by Germany and NATO allies from 2023.",
      pl: "Leopard 2A8 to najnowsza ewolucja rodziny Leopard 2, powstała w odpowiedzi na współczesne zagrożenia pola walki. Wyróżnia się ulepszonym pancerzem, systemem Trophy i zwiększoną siłą ognia. Zamówiony przez Niemcy i sojuszników NATO od 2023 roku.",
    },
  },
  {
    id: "97019",
    scale: "1:28",
    numbering: "97019",
    name: "E-100 Heavy Tank",
    recommendAge: "14+",
    pieces: 1883,
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "tanks_vehicles",
    faction: "germany",
    about: {
      en: "The E-100 Heavy Tank set recreates the German super-heavy tank design. Nearly 1900 pieces build a massive vehicle with the proposed 15 cm or 17 cm gun and the distinctive wide hull.",
      pl: "Zestaw E-100 Heavy Tank odtwarza niemiecki projekt superciężkiego czołgu. Prawie 1900 elementów buduje ogromny pojazd z planowanym działem 15 lub 17 cm i charakterystycznym szerokim kadłubem.",
    },
    historicalBackground: {
      en: "The E-100 was a German super-heavy tank design, competitor to the Maus. One prototype hull was built; it was captured by British forces in 1945. The design featured 200 mm front armor and was intended to mount a 15 cm or 17 cm main gun.",
      pl: "E-100 to niemiecki projekt superciężkiego czołgu, konkurent Mausa. Zbudowano jeden kadłub prototypowy; zdobyli go Brytyjczycy w 1945. Projekt zakładał pancerz czołowy 200 mm i działonówkę 15 lub 17 cm.",
    },
  },
  {
    id: "97016",
    scale: "1:28",
    numbering: "97016",
    name: "Panzer VIII Maus",
    recommendAge: "14+",
    pieces: 2416,
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "tanks_vehicles",
    faction: "germany",
    about: {
      en: "The Panzer VIII Maus set recreates the heaviest tank ever built. Over 2400 pieces build the massive 188-ton vehicle with its twin-gun turret and distinctive rounded hull, faithfully detailed for builders.",
      pl: "Zestaw Panzer VIII Maus odtwarza najcięższy czołg w historii. Ponad 2400 elementów buduje 188-tonowy pojazd z dwudziałową wieżą i charakterystycznym zaokrąglonym kadłubem.",
    },
    historicalBackground: {
      en: "The Panzer VIII Maus was a German super-heavy tank, the heaviest fully enclosed armored fighting vehicle ever built. Only two prototypes were completed. At 188 tons, it was nearly immune to most anti-tank weapons but proved impractical for production.",
      pl: "Panzer VIII Maus to niemiecki superciężki czołg, najcięższy całkowicie opancerzony pojazd bojowy w historii. Ukończono tylko dwa prototypy. Przy masie 188 ton był niemal odporny na większość broni przeciwpancernej, ale niepraktyczny w produkcji.",
    },
  },
  {
    id: "97010",
    scale: "Other",
    numbering: "97010",
    name: "M4 Sherman Medium Tank",
    recommendAge: "6+",
    pieces: 1682,
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "tanks_vehicles",
    faction: "usa",
    about: {
      en: "The M4 Sherman set recreates the iconic American medium tank of World War II. Over 1600 pieces build a detailed model with the characteristic hull shape and turret that made the Sherman the backbone of Allied armored forces.",
      pl: "Zestaw M4 Sherman odtwarza ikoniczny amerykański czołg średni z II wojny światowej. Ponad 1600 elementów buduje szczegółowy model z charakterystycznym kadłubem i wieżą.",
    },
    historicalBackground: {
      en: "The M4 Sherman was the primary American tank of WWII and one of the most produced tanks in history (nearly 50,000 units). It served in all theaters and with many Allied nations. Reliable and easy to maintain, it formed the backbone of Western Allied armored divisions.",
      pl: "M4 Sherman to podstawowy amerykański czołg II wojny światowej i jeden z najliczniej produkowanych w historii (prawie 50 000 sztuk). Służył na wszystkich frontach i w armiach wielu aliantów.",
    },
  },
  {
    id: "98005",
    numbering: "98005",
    name: "Bismarck Class Battleship",
    recommendAge: "6+",
    pieces: 2686,
    scale: "1:300",
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "ships",
    faction: "germany",
    about: {
      en: "The Bismarck Class Battleship set recreates the legendary German battleship from World War II. Over 2600 pieces build a detailed 1:300 scale model with the distinctive superstructure and main armament that made Bismarck one of the most powerful warships of its era.",
      pl: "Zestaw Bismarck Class Battleship odtwarza legendarny niemiecki pancernik z II wojny światowej. Ponad 2600 elementów buduje szczegółowy model w skali 1:300 z charakterystyczną nadbudówką i uzbrojeniem głównym.",
    },
    historicalBackground: {
      en: "Bismarck was a German battleship, the lead ship of her class. Commissioned in 1940, she and her sister Tirpitz were the largest battleships ever built in Europe. Bismarck famously sank HMS Hood in May 1941 before being sunk by the Royal Navy.",
      pl: "Bismarck to niemiecki pancernik, okręt wiodący swej klasy. Wcielony do służby w 1940 r., wraz z siostrzanym Tirpitzem były największymi pancernikami zbudowanymi w Europie. W maju 1941 Bismarck zatopił HMS Hood, zanim sam został zatopiony przez Royal Navy.",
    },
  },
  {
    id: "96001",
    scale: "Other",
    numbering: "96001",
    name: "KF51 Panther",
    recommendAge: "6+",
    pieces: 2235,
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "tanks_vehicles",
    faction: "germany",
    about: {
      en: "The KF51 Panther set recreates the next-generation German main battle tank concept. Over 2200 pieces build a detailed model with the unmanned turret, advanced optics, and modern armor configuration of this Rheinmetall design.",
      pl: "Zestaw KF51 Panther odtwarza koncepcję niemieckiego czołgu podstawowego nowej generacji. Ponad 2200 elementów buduje szczegółowy model z bezzałogową wieżą, zaawansowaną optyką i nowoczesnym pancerzem projektu Rheinmetall.",
    },
    historicalBackground: {
      en: "The KF51 Panther was unveiled by Rheinmetall in 2022 as a next-generation main battle tank. It features an unmanned turret with 130 mm gun, active protection systems, and modular design for future upgrades.",
      pl: "KF51 Panther został zaprezentowany przez Rheinmetall w 2022 r. jako czołg podstawowy nowej generacji. Wyróżnia się bezzałogową wieżą z działem 130 mm, systemami obrony aktywnej i modułową konstrukcją.",
    },
  },
  {
    id: "97022",
    scale: "1:28",
    numbering: "97022",
    name: "Jagdpanzer Tiger/P",
    recommendAge: "14+",
    pieces: 2125,
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "tanks_vehicles",
    faction: "germany",
    about: {
      en: "The Jagdpanzer Tiger/P set recreates the German heavy tank destroyer also known as Ferdinand. Over 2100 pieces build a detailed 1:28 scale model with the massive 8.8 cm PaK 43 and distinctive casemate hull.",
      pl: "Zestaw Jagdpanzer Tiger/P odtwarza niemiecki ciężki niszczyciel czołgów znany także jako Ferdinand. Ponad 2100 elementów buduje szczegółowy model w skali 1:28 z potężnym działem 8.8 cm PaK 43.",
    },
    historicalBackground: {
      en: "Jagdpanzer Tiger (P), nicknamed Ferdinand after its designer, was a German tank destroyer using the Porsche Tiger chassis. It saw action at Kursk in 1943. Survivors were upgraded and renamed Elefant.",
      pl: "Jagdpanzer Tiger (P), nazywany Ferdinandem od nazwiska konstruktora, to niemiecki niszczyciel czołgów na podwoziu Tygrysa Porsche. Wziął udział w bitwie pod Kurskiem w 1943 r. Przebudowane egzemplarze nosiły nazwę Elefant.",
    },
  },
  {
    id: "97023",
    scale: "1:28",
    numbering: "97023",
    name: "Jagdpanzer Tiger/P (Elephant)",
    recommendAge: "14+",
    pieces: 1750,
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "tanks_vehicles",
    faction: "germany",
    about: {
      en: "The Jagdpanzer Tiger/P (Elephant) set recreates the upgraded German tank destroyer. Nearly 1800 pieces build a 1:28 scale model with the improved hull and 8.8 cm gun that earned it the Elephant nickname.",
      pl: "Zestaw Jagdpanzer Tiger/P (Elephant) odtwarza zmodernizowany niemiecki niszczyciel czołgów. Prawie 1800 elementów buduje model w skali 1:28 z ulepszonym kadłubem i działem 8.8 cm.",
    },
    historicalBackground: {
      en: "Elefant was the upgraded name for surviving Ferdinands after modifications in 1943–1944. Improvements included a machine gun, better armor, and Commander's cupola. Used in Italy and on the Eastern Front.",
      pl: "Elefant to nazwa nadana przebudowanym Ferdinandom po modernizacji w latach 1943–1944. Zmiany obejmowały karabin maszynowy, lepszy pancerz i wieżyczkę dowódcy. Używane we Włoszech i na froncie wschodnim.",
    },
  },
  {
    id: "98009",
    numbering: "98009",
    name: "USS MONTANA",
    recommendAge: "14+",
    pieces: 2956,
    scale: "1:300",
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "ships",
    faction: "usa",
    about: {
      en: "The USS Montana set recreates the planned American super-battleship that was never completed. Nearly 3000 pieces build a 1:300 scale model of what would have been the largest US battleship class.",
      pl: "Zestaw USS Montana odtwarza planowany amerykański superpancernik, który nigdy nie wszedł do służby. Prawie 3000 elementów buduje model w skali 1:300 największej planowanej klasy pancerników USA.",
    },
    historicalBackground: {
      en: "The Montana class was a planned class of five US Navy battleships, designed to succeed the Iowa class. Larger and more heavily armed, construction was cancelled in 1943 in favor of aircraft carriers. None were built.",
      pl: "Klasa Montana to planowana seria pięciu pancerników US Navy, następców Iowa. Większe i silniej uzbrojone – budowę anulowano w 1943 r. na rzecz lotniskowców. Żaden nie został zbudowany.",
    },
  },
  {
    id: "97021",
    scale: "1:28",
    numbering: "97021",
    name: "Jagdpanzer Tiger/P",
    recommendAge: "14+",
    pieces: 1702,
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "tanks_vehicles",
    faction: "germany",
    about: {
      en: "The Jagdpanzer Tiger/P set recreates another variant of the German heavy tank destroyer. Over 1700 pieces build a 1:28 scale model with the massive 8.8 cm gun and characteristic Porsche chassis.",
      pl: "Zestaw Jagdpanzer Tiger/P odtwarza kolejną wersję niemieckiego ciężkiego niszczyciela czołgów. Ponad 1700 elementów buduje model w skali 1:28 z potężnym działem 8.8 cm i charakterystycznym podwoziem Porsche.",
    },
    historicalBackground: {
      en: "Multiple Jagdpanzer Tiger (P) variants existed with different production runs. All shared the Porsche Tiger chassis and 8.8 cm PaK 43/2 L/71 gun. The design prioritized firepower and frontal armor.",
      pl: "Istniało kilka wersji Jagdpanzer Tiger (P) z różnych serii produkcyjnych. Wspólne były podwozie Tygrysa Porsche i armata 8.8 cm PaK 43/2 L/71. Projekt priorytetyzował siłę ognia i pancerz czołowy.",
    },
  },
  {
    id: "98010",
    numbering: "98010",
    name: "USS Colorado",
    recommendAge: "6+",
    pieces: 2326,
    scale: "1:300",
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "ships",
    faction: "usa",
    about: {
      en: "The USS Colorado set recreates the American battleship of the Colorado class. Over 2300 pieces build a 1:300 scale model with the distinctive 16-inch guns that made this class among the most powerful of the interwar period.",
      pl: "Zestaw USS Colorado odtwarza amerykański pancernik klasy Colorado. Ponad 2300 elementów buduje model w skali 1:300 z charakterystycznymi działami 16 cali.",
    },
    historicalBackground: {
      en: "USS Colorado was the lead ship of the Colorado class, commissioned in 1923. She was one of the \"Big Five\" treaty battleships. She served in the Pacific during WWII, surviving Pearl Harbor and supporting island campaigns.",
      pl: "USS Colorado był okrętem wiodącym klasy Colorado, wcielony do służby w 1923 r. Jeden z „Wielkiej Piątki” pancerników traktatowych. Służył na Pacyfiku podczas II wojny światowej.",
    },
  },
  {
    id: "97006",
    scale: "1:28",
    numbering: "97006",
    name: "IS-2 Heavy Tank",
    recommendAge: "6+",
    pieces: 1402,
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "tanks_vehicles",
    faction: "ussr",
    about: {
      en: "The IS-2 Heavy Tank set recreates the iconic Soviet heavy tank of World War II. Over 1400 pieces build a detailed model with the massive 122 mm D-25T gun and sloped armor that made the IS-2 a fearsome opponent.",
      pl: "Zestaw IS-2 Heavy Tank odtwarza ikoniczny sowiecki czołg ciężki z II wojny światowej. Ponad 1400 elementów buduje szczegółowy model z potężną armatą 122 mm D-25T i pochylonym pancerzem.",
    },
    historicalBackground: {
      en: "The IS-2 was a Soviet heavy tank, the most powerful Allied tank of WWII. Over 3800 were built. Its 122 mm gun could destroy any German tank. Used from 1944 in the final drives into Germany.",
      pl: "IS-2 to sowiecki czołg ciężki, najpotężniejszy czołg aliancki II wojny. Zbudowano ponad 3800 egzemplarzy. Armata 122 mm niszczyła każdy niemiecki czołg. Używany od 1944 r. w marszu na Niemcy.",
    },
  },
  {
    id: "97031",
    scale: "1:28",
    numbering: "97031",
    name: "Wirbelwind Flakpanzer IV",
    recommendAge: "6+",
    pieces: 1474,
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "tanks_vehicles",
    faction: "germany",
    about: {
      en: "The Wirbelwind Flakpanzer IV set recreates the German self-propelled anti-aircraft vehicle based on the Panzer IV chassis. Over 1400 pieces build a 1:28 scale model with the distinctive quadruple 2 cm Flak 38 turret.",
      pl: "Zestaw Wirbelwind Flakpanzer IV odtwarza niemiecki samobieżny wóz przeciwlotniczy na podwoziu Panzer IV. Ponad 1400 elementów buduje model w skali 1:28 z charakterystyczną czterolufową wieżą 2 cm Flak 38.",
    },
    historicalBackground: {
      en: "Wirbelwind (Whirlwind) was a German anti-aircraft tank, created by mounting a quadruple 2 cm Flak 38 on a Panzer IV chassis. About 105 were converted in 1944. Effective against low-flying aircraft.",
      pl: "Wirbelwind to niemiecki czołg przeciwlotniczy – na podwoziu Panzer IV montowano poczwórną armatę 2 cm Flak 38. Około 105 przebudowano w 1944 r. Skuteczny przeciw samolotom niskiego pułapu.",
    },
  },
  {
    id: "97032",
    scale: "1:28",
    numbering: "97032",
    name: "Type 99B Main Battle Tank",
    recommendAge: "14+",
    pieces: 1916,
    blockSize: "12.5 × 16 × 39.6 cm",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "tanks_vehicles",
    faction: "china",
  },
  {
    id: "97033",
    scale: "1:28",
    numbering: "97033",
    name: "Type 100",
    recommendAge: "6+",
    pieces: 2222,
    blockSize: "34.1 × 12.9 × 15.8 cm",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "tanks_vehicles",
    faction: "china",
    about: {
      en: "The Type 100 set builds a detailed brick model with the presence of a contemporary Chinese-style main battle tank. With 2222 pieces and finished dimensions of roughly 34.1 × 12.9 × 15.8 cm, it suits ages 6+ and works well as a bold shelf display for collectors.",
      pl: "Zestaw Type 100 pozwala złożyć rozbudowany model z klocków o sylwetce współczesnego chińskiego MBT. 2222 elementy oraz gabaryty ok. 34.1 × 12.9 × 15.8 cm; zalecany wiek 6+. Dobry jako efektowna pozycja na półkę dla kolekcjonerów.",
      es: "El set Type 100 permite montar un modelo detallado en bloques con la presencia de un carro de combate principal chino contemporáneo. 2222 piezas y dimensiones aproximadas de 34.1 × 12.9 × 15.8 cm; edad recomendada 6+. Ideal como pieza llamativa para exposición.",
      de: "Das Type-100-Set ergibt ein detailreiches Klemmbausteinmodell mit der Präsenz eines zeitgenössischen chinesischen Kampfpanzers. 2222 Teile und Maße von etwa 34.1 × 12.9 × 15.8 cm; empfohlen ab 6 Jahren. Eignet sich als markantes Regalstück für Sammler.",
      fr: "Le set Type 100 assemble une maquette détaillée en briques au gabarit d’un char de combat principal chinois contemporain. 2222 pièces et dimensions d’environ 34.1 × 12.9 × 15.8 cm ; à partir de 6 ans. Convient comme pièce forte pour une vitrine ou une étagère.",
      nl: "De Type 100-set levert een gedetailleerd bouwstenenmodel met het karakter van een hedendaagse Chinese hoofdgevechtstank. 2222 onderdelen en afmetingen van circa 34.1 × 12.9 × 15.8 cm; vanaf 6 jaar. Geschikt als opvallend pronkstuk voor verzamelaars.",
      zh: "Type 100 套装可拼装一款细节丰富的积木模型，造型贴近中国当代主战坦克体量与气势。共 2222 颗粒，完成后尺寸约 34.1 × 12.9 × 15.8 cm，建议年龄 6 岁以上，适合作为陈列架上醒目的收藏展示作品。",
    },
    historicalBackground: {
      en: "China’s tank industry moved from licensed types toward indigenous designs that combine composite armor, stabilized main guns, and modern fire-control suites. Kits like this help illustrate how industrial capacity and doctrine shaped successive generations of main battle tanks.",
      pl: "Chiński przemysł czołgowy przeszedł od licencji ku rodzimym konstrukcjom łączącym pancerz kompozytowy, stabilizowane działa i nowoczesne systemy naprowadzania. Takie zestawy pomagają zobaczyć, jak możliwości przemysłu i doktryna kształtowały kolejne pokolenia MBT.",
      es: "La industria china evolucionó de diseños bajo licencia hacia carros plenamente nacionales con blindaje compuesto, cañón estabilizado y electrónica moderna de control de tiro. Maquetas como esta ayudan a entender cómo la capacidad industrial y la doctrina moldearon generaciones sucesivas de MBT.",
      de: "Chinas Panzerindustrie entwickelte sich von lizenzierten Mustern zu eigenständigen Entwürfen mit Verbundpanzerung, stabilisierten Hauptgeschützen und moderner Feuerleitelektronik. Solche Modelle erläutern, wie Industriekapazität und Doktrin aufeinanderfolgende Kampfpanzer-Generationen prägten.",
      fr: "L’industrie chinoise est passée de modèles sous licence à des chars entièrement nationaux alliant blindage composite, canon stabilisé et électronique de tir moderne. Des maquettes de ce type montrent comment capacité industrielle et doctrine ont façonné les générations successives de MBT.",
      nl: "De Chinese tankindustrie groeide van gelicentieerde ontwerpen naar eigen modellen met composietpantser, gestabiliseerde hoofdbewapening en moderne vuurleiding. Bouwpakketten als dit tonen hoe industriële basis en doctrine opeenvolgende generaties hoofdgevechtstanks vormden.",
      zh: "中国坦克工业经历了由引进仿制到自主研制的转变，新一代战车普遍整合复合装甲、稳定坦克炮与现代化火控体系。此类题材模型有助于从工业基础与编制运用的角度理解主战坦克代际演进的背景。",
    },
  },
  {
    id: "97039",
    scale: "1:28",
    numbering: "97039",
    name: "STRV 103B",
    recommendAge: "14+",
    pieces: 1773,
    blockSize: "normal",
    productSize: "32.3 × 14 × 12.5 cm",
    packageSize: "42 × 28.5 × 11.5 cm",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "tanks_vehicles",
    faction: "sweden",
    about: {
      en: "The STRV 103B set recreates Sweden's unique turretless main battle tank in 1773 compatible bricks.",
      pl: "Zestaw STRV 103B odtwarza szwedzki czołg podstawowy bez wieży — Stridsvagn 103B — w 1773 elementach.",
    },
  },
  {
    id: "97037",
    scale: "Other",
    numbering: "97037",
    name: "Rivers Gantry Crane",
    recommendAge: "6+",
    pieces: 2803,
    blockSize: "46 × 17 × 36 cm",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "other",
    faction: "germany",
  },
  {
    id: "97029",
    scale: "1:28",
    numbering: "97029",
    name: "Tiger (P)",
    recommendAge: "6+",
    pieces: 1656,
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "tanks_vehicles",
    faction: "germany",
  },
  {
    id: "95008",
    scale: "Other",
    numbering: "95008",
    name: "Tiger Tank Engine",
    recommendAge: "14+",
    pieces: 1236,
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "other",
    faction: "germany",
    about: {
      en: "The Tiger Tank Engine set recreates the Maybach HL 230 engine that powered the Tiger I and Tiger II tanks. Over 1200 pieces build a detailed model showing the internal mechanics of this legendary powerplant.",
      pl: "Zestaw Tiger Tank Engine odtwarza silnik Maybach HL 230, który napędzał czołgi Tiger I i Tiger II. Ponad 1200 elementów buduje szczegółowy model mechanizmów tej legendarnej jednostki napędowej.",
    },
    historicalBackground: {
      en: "The Maybach HL 230 P45 was a V-12 gasoline engine producing 700 hp. It powered the Tiger II and late Tiger I. Over 9000 were produced. Known for power and complexity.",
      pl: "Maybach HL 230 P45 to 12-cylindrowy silnik benzynowy o mocy 700 KM. Napędzał Tiger II i późne Tiger I. Wyprodukowano ponad 9000 sztuk. Znany z mocy i złożoności.",
    },
  },
  {
    id: "97007",
    scale: "1:28",
    numbering: "97007",
    name: "T34-85 Medium Tank",
    recommendAge: "6+",
    pieces: 1203,
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "tanks_vehicles",
    faction: "ussr",
    about: {
      en: "The T34-85 Medium Tank set recreates the legendary Soviet medium tank that became a symbol of victory. Over 1200 pieces build a detailed model with the upgraded 85 mm gun turret that decisively improved its combat capability.",
      pl: "Zestaw T34-85 Medium Tank odtwarza legendarny sowiecki czołg średni, symbol zwycięstwa. Ponad 1200 elementów buduje szczegółowy model z ulepszoną wieżą z armatą 85 mm.",
    },
    historicalBackground: {
      en: "The T-34-85 was an upgrade of the T-34, with a larger three-man turret and 85 mm gun. Over 35,000 were built. It matched or exceeded German tanks and became the backbone of the Red Army.",
      pl: "T-34-85 to modernizacja T-34 z większą trzyosobową wieżą i armatą 85 mm. Zbudowano ponad 35 000 sztuk. Dorównywał lub przewyższał czołgi niemieckie i był filarem Armii Czerwonej.",
    },
  },
  {
    id: "97030",
    scale: "1:28",
    numbering: "97030",
    name: "Panzer IV H",
    recommendAge: "6+",
    pieces: 1641,
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "tanks_vehicles",
    faction: "germany",
    about: {
      en: "The Panzer IV H set recreates the most produced variant of the German medium tank. Over 1600 pieces build a 1:28 scale model with the long 7.5 cm KwK 40 gun and side skirt armor.",
      pl: "Zestaw Panzer IV H odtwarza najliczniej produkowaną wersję niemieckiego czołgu średniego. Ponad 1600 elementów buduje model w skali 1:28 z długą armatą 7.5 cm KwK 40 i fartuchami pancernymi.",
    },
    historicalBackground: {
      en: "The Panzer IV Ausf. H was the most built variant (over 3000). Introduced in 1943 with 80 mm frontal armor, long 7.5 cm gun, and Zimmerit coating. Served until the end of the war.",
      pl: "Panzer IV Ausf. H był najliczniejszą wersją (ponad 3000 sztuk). Wprowadzony w 1943 r. z pancerzem czołowym 80 mm, długą armatą 7.5 cm i powłoką Zimmerit. Służył do końca wojny.",
    },
  },
  {
    id: "96006",
    scale: "Other",
    numbering: "96006",
    name: "Armored Vehicles Stryker X",
    recommendAge: "6+",
    pieces: 2005,
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "tanks_vehicles",
    faction: "usa",
    about: {
      en: "The Armored Vehicles Stryker X set recreates the American 8x8 armored combat vehicle in an upgraded configuration. Over 2000 pieces build a detailed model with the remote weapon station and modular armor.",
      pl: "Zestaw Armored Vehicles Stryker X odtwarza amerykański 8x8 opancerzony wóz bojowy w ulepszonej konfiguracji. Ponad 2000 elementów buduje szczegółowy model ze zdalnie sterowaną wieżą i modułowym pancerzem.",
    },
    historicalBackground: {
      en: "The Stryker is an American family of 8x8 armored vehicles in service since 2002. The Stryker X is a concept showcasing future upgrades: 50 mm gun, improved protection, and hybrid-electric drive.",
      pl: "Stryker to amerykańska rodzina opancerzonych wozów 8x8 w służbie od 2002 r. Stryker X to koncepcja przyszłych ulepszeń: armata 50 mm, lepsza ochrona i napęd hybrydowo-elektryczny.",
    },
  },
  {
    id: "97020",
    scale: "1:28",
    numbering: "97020",
    name: "E-100 Heavy Tank",
    recommendAge: "10+",
    pieces: 1866,
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "tanks_vehicles",
    faction: "germany",
    about: {
      en: "The E-100 Heavy Tank set recreates the German super-heavy tank design in the Maus turret version. Nearly 1900 pieces build a 1:28 scale model with the massive turret and wide hull of this never-completed prototype.",
      pl: "Zestaw E-100 Heavy Tank odtwarza niemiecki projekt superciężkiego czołgu w wersji z wieżą Maus. Prawie 1900 elementów buduje model w skali 1:28 z masywną wieżą i szerokim kadłubem.",
    },
    historicalBackground: {
      en: "The E-100 was a competitor to the Maus. One hull was built; it was to mount either a Maus turret or a lighter variant. Captured by the British in 1945, the hull was scrapped post-war.",
      pl: "E-100 był konkurentem Mausa. Zbudowano jeden kadłub; planowano montaż wieży Maus lub lżejszej wersji. Zdobyty przez Brytyjczyków w 1945 r., kadłub zezłomowano po wojnie.",
    },
  },
  {
    id: "98006",
    numbering: "98006",
    name: "Tirpitz Battleship",
    recommendAge: "14+",
    pieces: 2738,
    scale: "1:300",
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "ships",
    faction: "germany",
    about: {
      en: "The Tirpitz Battleship set recreates the sister ship of Bismarck, the largest European battleship ever built. Over 2700 pieces build a 1:300 scale model of this legendary „Lone Queen of the North”.",
      pl: "Zestaw Tirpitz Battleship odtwarza siostrzany okręt Bismarcka, największy pancernik zbudowany w Europie. Ponad 2700 elementów buduje model w skali 1:300 legendarnej „Samotnej Królowej Północy”.",
    },
    historicalBackground: {
      en: "Tirpitz was Bismarck's sister ship, deployed to Norway in 1942. She tied down Allied naval resources and was the target of multiple attacks. Sunk in 1944 by RAF Tallboy bombs in a Norwegian fjord.",
      pl: "Tirpitz był siostrzanym okrętem Bismarcka, stacjonował w Norwegii od 1942 r. Wiązał siły morskie aliantów i był celem wielu ataków. Zatopiony w 1944 r. bombami Tallboy RAF w norweskim fjordzie.",
    },
  },
  {
    id: "96003",
    scale: "Other",
    numbering: "96003",
    name: "T-14 Armata Main Battle Tank",
    recommendAge: "10+",
    pieces: 2300,
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "tanks_vehicles",
    faction: "ussr",
    about: {
      en: "The T-14 Armata Main Battle Tank set recreates Russia's new-generation main battle tank. Over 2300 pieces build a detailed model with the unmanned turret, 125 mm smoothbore gun, and revolutionary layout.",
      pl: "Zestaw T-14 Armata Main Battle Tank odtwarza rosyjski czołg podstawowy nowej generacji. Ponad 2300 elementów buduje szczegółowy model z bezzałogową wieżą, armatą gładkolufową 125 mm i rewolucyjnym układem.",
    },
    historicalBackground: {
      en: "The T-14 Armata was unveiled in 2015. It features an unmanned turret with crew in an armored capsule, 125 mm 2A82 gun, and Afghanit active protection. First new Russian MBT design since the T-72.",
      pl: "T-14 Armata został zaprezentowany w 2015 r. Ma bezzałogową wieżę z załogą w opancerzonej kapsule, armatę 2A82 125 mm i system Afghanit. Pierwszy nowy rosyjski czołg od T-72.",
    },
  },
  {
    id: "97012",
    scale: "1:28",
    numbering: "97012",
    name: "Wittmann S04 White Tiger Tank",
    recommendAge: "10+",
    pieces: 2000,
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "tanks_vehicles",
    faction: "germany",
    about: {
      en: "The Wittmann S04 White Tiger Tank set recreates Michael Wittmann's legendary Tiger I. Over 2000 pieces build a detailed model of the tank in which the most famous German tank ace achieved many of his victories.",
      pl: "Zestaw Wittmann S04 White Tiger Tank odtwarza legendarny Tiger I Michaela Wittmanna. Ponad 2000 elementów buduje szczegółowy model czołgu, na którym najsłynniejszy niemiecki as pancerny odniósł wiele zwycięstw.",
    },
    historicalBackground: {
      en: "Michael Wittmann was Germany's top tank ace with over 138 tank kills. His Tiger 007 („White Tiger”) became iconic. He was killed in August 1944 near Saint-Aignan during the Normandy campaign.",
      pl: "Michael Wittmann był najskuteczniejszym niemieckim asem pancernym z ponad 138 zniszczonymi czołgami. Jego Tiger 007 („Biały Tygrys”) stał się ikoną. Zginął w sierpniu 1944 r. pod Saint-Aignan w Normandii.",
    },
  },
  // Strona 2
  {
    id: "96007",
    scale: "Other",
    numbering: "96007",
    name: "M2 Bradley",
    recommendAge: "10+",
    pieces: 1900,
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "tanks_vehicles",
    faction: "usa",
    about: {
      en: "The M2 Bradley set recreates the American infantry fighting vehicle. Nearly 1900 pieces build a detailed model with the 25 mm chain gun, TOW missile launcher, and amphibious hull that has served since 1981.",
      pl: "Zestaw M2 Bradley odtwarza amerykański bojowy wóz piechoty. Prawie 1900 elementów buduje szczegółowy model z działem 25 mm, wyrzutnią TOW i amfibijnym kadłubem – w służbie od 1981 r.",
    },
    historicalBackground: {
      en: "The M2 Bradley IFV entered service in 1981. Over 6700 were built. It carries six infantry and mounts a 25 mm Bushmaster cannon and TOW missiles. Served in Iraq, Afghanistan, and Gulf War.",
      pl: "M2 Bradley IFV wszedł do służby w 1981 r. Zbudowano ponad 6700 sztuk. Przewozi sześciu żołnierzy i ma działko 25 mm Bushmaster oraz pociski TOW. Służył w Iraku, Afganistanie i wojnie w Zatoce.",
    },
  },
  {
    id: "96005",
    scale: "Other",
    numbering: "96005",
    name: "Abrams-X",
    recommendAge: "10+",
    pieces: 2600,
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "tanks_vehicles",
    faction: "usa",
    about: {
      en: "The Abrams-X set recreates the next-generation American main battle tank concept. Over 2600 pieces build a detailed model with the unmanned turret, 120 mm XM360 gun, and hybrid-electric propulsion.",
      pl: "Zestaw Abrams-X odtwarza koncepcję amerykańskiego czołgu podstawowego nowej generacji. Ponad 2600 elementów buduje szczegółowy model z bezzałogową wieżą, armatą 120 mm XM360 i napędem hybrydowym.",
    },
    historicalBackground: {
      en: "AbramsX is General Dynamics' technology demonstrator unveiled in 2022. It reduces crew to three, adds hybrid-electric drive for fuel savings, and integrates AI and autonomous capabilities.",
      pl: "AbramsX to demonstrator technologii General Dynamics z 2022 r. Zmniejsza załogę do trzech, dodaje napęd hybrydowo-elektryczny i integruje AI oraz możliwości autonomiczne.",
    },
  },
  {
    id: "97003",
    scale: "1:28",
    numbering: "97003",
    name: "PzKpfw IV Ausf. F2",
    recommendAge: "10+",
    pieces: 1900,
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "tanks_vehicles",
    faction: "germany",
    about: {
      en: "The PzKpfw IV Ausf. F2 set recreates the upgunned German medium tank that first mounted the long 7.5 cm gun. Nearly 1900 pieces build a detailed model of this crucial upgrade to the Panzer IV.",
      pl: "Zestaw PzKpfw IV Ausf. F2 odtwarza zmodernizowany niemiecki czołg średni z długą armatą 7.5 cm. Prawie 1900 elementów buduje szczegółowy model tej kluczowej modernizacji Panzer IV.",
    },
    historicalBackground: {
      en: "The Panzer IV Ausf. F2, introduced in 1942, replaced the short 7.5 cm with the long L/43 gun. This made the Panzer IV effective against T-34 and KV tanks. A turning point for the design.",
      pl: "Panzer IV Ausf. F2 z 1942 r. zamienił krótką armatę 7.5 cm na długą L/43. To pozwoliło skutecznie zwalczać T-34 i KV. Przełom w rozwoju konstrukcji.",
    },
  },
  {
    id: "97025",
    scale: "1:28",
    numbering: "97025",
    name: "Panzer VIII Maus",
    recommendAge: "14+",
    pieces: 2688,
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "tanks_vehicles",
    faction: "germany",
    about: {
      en: "The Panzer VIII Maus set recreates the heaviest tank ever built in a different version. Over 2600 pieces build a 1:28 scale model with the massive twin-gun turret and distinctive rounded hull.",
      pl: "Zestaw Panzer VIII Maus odtwarza najcięższy czołg w historii w innej wersji. Ponad 2600 elementów buduje model w skali 1:28 z masywną dwudziałową wieżą i charakterystycznym zaokrąglonym kadłubem.",
    },
    historicalBackground: {
      en: "The Panzer VIII Maus reached 188 tons. Two prototypes were completed. The design combined a 12.8 cm and 7.5 cm gun in the turret. Both were captured by the Soviets; one is in Kubinka Museum.",
      pl: "Panzer VIII Maus osiągał 188 ton. Ukończono dwa prototypy. Wieża mieściła armaty 12.8 cm i 7.5 cm. Oba zdobyli Sowieci; jeden jest w muzeum w Kubince.",
    },
  },
  {
    id: "97015",
    scale: "1:28",
    numbering: "97015",
    name: "Franz Staudegger 1322 German Tiger Tank",
    recommendAge: "10+",
    pieces: 2000,
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "tanks_vehicles",
    faction: "germany",
    about: {
      en: "The Franz Staudegger 1322 German Tiger Tank set recreates the Tiger I commanded by tank ace Franz Staudegger. Over 2000 pieces build a detailed model of this vehicle that achieved legendary exploits on the Eastern Front.",
      pl: "Zestaw Franz Staudegger 1322 German Tiger Tank odtwarza Tiger I dowodzony przez asa pancernego Franza Staudeggera. Ponad 2000 elementów buduje szczegółowy model pojazdu z legendarnymi wyczynami na froncie wschodnim.",
    },
    historicalBackground: {
      en: "Franz Staudegger was a German Tiger ace. In July 1943, his single Tiger reportedly engaged and destroyed 22 Soviet T-34s. He received the Knight's Cross. His tank bore tactical number 1322.",
      pl: "Franz Staudegger był niemieckim asem na Tygrysie. W lipcu 1943 r. jego pojedynczy Tiger miał zniszczyć 22 sowieckie T-34. Otrzymał Krzyż Rycerski. Jego czołg miał numer taktyczny 1322.",
    },
  },
  {
    id: "95005",
    scale: "Other",
    numbering: "95005",
    name: "Car series",
    recommendAge: "14+",
    pieces: 1726,
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "civilian",
  },
  {
    id: "95020",
    scale: "Other",
    numbering: "95020",
    name: "F1 Racing Car Transporter",
    recommendAge: "14+",
    pieces: 2135,
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "civilian",
    seoCategory: "vehicle",
  },
  {
    id: "95025",
    scale: "Other",
    numbering: "95025",
    name: "F1 Racing Pit Garage",
    recommendAge: "14+",
    pieces: 2035,
    blockSize: "normal",
    productSize: "50 × 23.5 × 14.5 cm",
    material: "Eco-friendly ABS plastic",
    category: "civilian",
    seoCategory: "set",
  },
  {
    id: "95026",
    scale: "Other",
    numbering: "95026",
    name: "Racing Pit Lane",
    recommendAge: "14+",
    pieces: 1413,
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "civilian",
    seoCategory: "set",
  },
  {
    id: "95021",
    scale: "Other",
    numbering: "95021",
    name: "Car series",
    recommendAge: "14+",
    pieces: 1536,
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "civilian",
    seoCategory: "vehicle",
  },
  {
    id: "95022",
    scale: "Other",
    numbering: "95022",
    name: "Car series",
    recommendAge: "14+",
    pieces: 1294,
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "civilian",
    seoCategory: "vehicle",
  },
  {
    id: "95023",
    scale: "Other",
    numbering: "95023",
    name: "Garage",
    recommendAge: "14+",
    pieces: 1047,
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "civilian",
    seoCategory: "set",
  },
  {
    id: "95027",
    scale: "Other",
    numbering: "95027",
    name: "Car Series",
    recommendAge: "14+",
    pieces: 876,
    blockSize: "normal",
    productSize: "22.3 × 8.7 × 15.5 cm",
    material: "Eco-friendly ABS plastic",
    category: "civilian",
    seoCategory: "vehicle",
  },
  {
    id: "95028",
    scale: "Other",
    numbering: "95028",
    name: "Car Series",
    recommendAge: "14+",
    pieces: 911,
    blockSize: "normal",
    productSize: "30 × 11 × 10 cm",
    material: "Eco-friendly ABS plastic",
    category: "civilian",
    seoCategory: "vehicle",
  },
  {
    id: "97036",
    scale: "1:16",
    numbering: "97036",
    name: "Tiger II Henschel Turret",
    recommendAge: "14+",
    pieces: 7261,
    blockSize: "normal",
    productSize: "63.8 × 25.3 × 21.7 cm",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "tanks_vehicles",
    faction: "germany",
    seoCategory: "tank",
  },
  {
    id: "97038",
    scale: "1:28",
    numbering: "97038",
    name: "FV4005 Stage II",
    recommendAge: "14+",
    pieces: 2080,
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "tanks_vehicles",
    faction: "uk",
  },
  {
    id: "97055",
    scale: "1:28",
    numbering: "97055",
    name: "Leopard 1 Medium Tank",
    recommendAge: "14+",
    pieces: 1862,
    blockSize: "normal",
    productSize: "35.2 × 15.2 × 13.2 cm",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "tanks_vehicles",
    faction: "germany",
    seoCategory: "tank",
  },
  {
    id: "97056",
    scale: "1:28",
    numbering: "97056",
    name: "FV215 Tank Destroyer",
    recommendAge: "6+",
    pieces: 2165,
    blockSize: "normal",
    productSize: "32.2 × 13.9 × 17.6 cm",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "tanks_vehicles",
    faction: "uk",
    seoCategory: "tankDestroyer",
  },
  {
    id: "97059",
    scale: "1:28",
    numbering: "97059",
    name: "Panther II",
    recommendAge: "14+",
    pieces: 1678,
    blockSize: "normal",
    productSize: "33.1 × 12.8 × 13.3 cm",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "tanks_vehicles",
    faction: "germany",
    seoCategory: "tank",
  },
  {
    id: "97060",
    scale: "1:28",
    numbering: "97060",
    name: "Jagdtiger",
    recommendAge: "14+",
    pieces: 1821,
    blockSize: "normal",
    productSize: "13.9 × 13.7 × 38.5 cm",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "tanks_vehicles",
    faction: "germany",
  },
  {
    id: "97066",
    scale: "1:28",
    numbering: "97066",
    name: "Sturmgeschütz IV",
    recommendAge: "14+",
    pieces: 1291,
    blockSize: "normal",
    productSize: "24.5 × 12.7 × 13.3 cm",
    packageSize: "42 × 28.5 × 11.5 cm",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "tanks_vehicles",
    faction: "germany",
    seoCategory: "tankDestroyer",
  },
  {
    id: "95009",
    scale: "Other",
    numbering: "95009",
    name: "T34-V2 Tank Engine",
    recommendAge: "14+",
    pieces: 2416,
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "other",
    faction: "ussr",
  },
  {
    id: "95010",
    scale: "Other",
    numbering: "95010",
    name: "GAA V8 Tank Engine",
    recommendAge: "14+",
    pieces: 1385,
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "other",
    faction: "usa",
  },
  {
    id: "95011",
    scale: "Other",
    numbering: "95011",
    name: "Supercar Engine",
    recommendAge: "6+",
    pieces: 612,
    blockSize: "normal",
    productSize: "14.5 × 12 × 11.5 cm",
    material: "Eco-friendly ABS plastic",
    category: "civilian",
    seoCategory: "engine",
  },
  {
    id: "95006",
    scale: "Other",
    numbering: "95006",
    name: "Car Series",
    recommendAge: "14+",
    pieces: 1216,
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "civilian",
  },
  {
    id: "95007",
    scale: "Other",
    numbering: "95007",
    name: "Car series",
    recommendAge: "6+",
    pieces: 1060,
    blockSize: "normal",
    packageSize: "42 × 25 × 9 cm",
    material: "Eco-friendly ABS plastic",
    category: "civilian",
  },
  {
    id: "95003",
    scale: "Other",
    numbering: "95003",
    name: "RV Set",
    recommendAge: "14+",
    pieces: 2135,
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "civilian",
    about: {
      en: "The RV Set recreates a detailed recreational vehicle for adventure on the road. Over 2100 pieces build a model with living quarters, cab, and all the details that make an RV a home away from home.",
      pl: "Zestaw RV Set odtwarza szczegółowy pojazd kempingowy na przygody w drodze. Ponad 2100 elementów buduje model z częścią mieszkalną, kabiną i detalami prawdziwego kampera.",
    },
  },
  {
    id: "97005",
    scale: "1:28",
    numbering: "97005",
    name: "KV-2 Heavy Tank",
    recommendAge: "10+",
    pieces: 2100,
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "tanks_vehicles",
    faction: "ussr",
    about: {
      en: "The KV-2 Heavy Tank set recreates the Soviet heavy tank with the massive 152 mm howitzer. Over 2100 pieces build a detailed model with the distinctive tall turret that made the KV-2 unmistakable on the battlefield.",
      pl: "Zestaw KV-2 Heavy Tank odtwarza sowiecki czołg ciężki z potężną haubicą 152 mm. Ponad 2100 elementów buduje szczegółowy model z charakterystyczną wysoką wieżą.",
    },
    historicalBackground: {
      en: "The KV-2 was a Soviet heavy assault tank. Its 152 mm M-10 howitzer could destroy any fortification. Fewer than 350 were built. Its tall turret made it vulnerable but psychologically terrifying to German troops.",
      pl: "KV-2 to sowiecki czołg szturmowy. Haubica 152 mm M-10 niszczyła każde umocnienie. Zbudowano mniej niż 350 sztuk. Wysoka wieża czyniła go wrażliwym, ale wywoływała strach wśród Niemców.",
    },
  },
  {
    id: "97002",
    scale: "1:28",
    numbering: "97002",
    name: "Panzer Medium G-Type Tank",
    recommendAge: "10+",
    pieces: 2000,
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "tanks_vehicles",
    faction: "germany",
    about: {
      en: "The Panzer Medium G-Type Tank set recreates the German medium tank prototype. Over 2000 pieces build a detailed model of this lesser-known design that reflected German armored vehicle development during the war.",
      pl: "Zestaw Panzer Medium G-Type Tank odtwarza niemiecki prototyp czołgu średniego. Ponad 2000 elementów buduje szczegółowy model mniej znanej konstrukcji z okresu rozwoju niemieckiej broni pancernej.",
    },
    historicalBackground: {
      en: "The Gerät (G-type) was a German medium tank project. Various prototypes and concepts were explored. The designation reflects the experimental nature of German armor development in the later war years.",
      pl: "Gerät (typ G) to niemiecki projekt czołgu średniego. Powstały różne prototypy i koncepcje. Oznaczenie odzwierciedla eksperymentalny charakter rozwoju niemieckiej broni pancernej.",
    },
  },
  {
    id: "96011",
    scale: "Other",
    numbering: "96011",
    name: "KF-41 Lynx IFV",
    recommendAge: "10+",
    pieces: 2000,
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "tanks_vehicles",
    faction: "germany",
    about: {
      en: "The KF-41 Lynx IFV set recreates the Hungarian-Rheinmetall infantry fighting vehicle. Over 2000 pieces build a detailed model with the Lance 2.0 turret and modular design for various mission roles.",
      pl: "Zestaw KF-41 Lynx IFV odtwarza węgiersko-niemiecki bojowy wóz piechoty Rheinmetall. Ponad 2000 elementów buduje szczegółowy model z wieżą Lance 2.0 i modułową konstrukcją.",
    },
    historicalBackground: {
      en: "The Lynx KF41 is a next-gen IFV developed by Rheinmetall (2016). It carries up to 9 troops and mounts a 35 mm or 50 mm cannon. Hungary and Australia have ordered it.",
      pl: "Lynx KF41 to IFV nowej generacji Rheinmetall (2016). Przewozi do 9 żołnierzy i ma armatę 35 lub 50 mm. Zamówiły go Węgry i Australia.",
    },
  },
  {
    id: "98008",
    numbering: "98008",
    name: "The North Carolina",
    recommendAge: "14+",
    pieces: 2662,
    scale: "1:300",
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "ships",
    faction: "usa",
    about: {
      en: "The North Carolina set recreates the American battleship that served in the Pacific. Over 2600 pieces build a 1:300 scale model of the first of the US fast battleships with its distinctive silhouette.",
      pl: "Zestaw The North Carolina odtwarza amerykański pancernik służący na Pacyfiku. Ponad 2600 elementów buduje model w skali 1:300 pierwszego amerykańskiego szybkiego pancernika.",
    },
    historicalBackground: {
      en: "USS North Carolina (BB-55) was the lead ship of her class, commissioned in 1941. She was the first new US battleship in nearly 20 years. Served throughout the Pacific War, earning 15 battle stars.",
      pl: "USS North Carolina (BB-55) był okrętem wiodącym swej klasy, wcielony w 1941 r. Pierwszy nowy pancernik USA od prawie 20 lat. Służył przez całą wojnę na Pacyfiku, 15 gwiazdek bojowych.",
    },
  },
  {
    id: "98007",
    numbering: "98007",
    name: "USS Missouri",
    recommendAge: "14+",
    pieces: 2568,
    scale: "1:300",
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "ships",
    faction: "usa",
    about: {
      en: "The USS Missouri set recreates the legendary battleship where Japan surrendered. Over 2500 pieces build a 1:300 scale model of the last US battleship, with the classic Iowa-class lines.",
      pl: "Zestaw USS Missouri odtwarza legendarny pancernik, na którego pokładzie podpisano kapitulację Japonii. Ponad 2500 elementów buduje model w skali 1:300 ostatniego pancernika USA.",
    },
    historicalBackground: {
      en: "USS Missouri (BB-63) was an Iowa-class battleship. The Japanese surrender was signed on her deck on September 2, 1945. She served in WWII, Korea, and the Gulf War. Now a museum ship in Pearl Harbor.",
      pl: "USS Missouri (BB-63) to pancernik klasy Iowa. Kapitulacja Japonii została podpisana na jego pokładzie 2 września 1945 r. Służył w II wojnie, Korei i Zatoce Perskiej. Obecnie okręt-muzeum w Pearl Harbor.",
    },
  },
  {
    id: "98002",
    numbering: "98002",
    name: "Panzer VIII Maus Land Cruiser Version",
    recommendAge: "6+",
    pieces: 3300,
    scale: "1:28",
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "tanks_vehicles",
    faction: "germany",
    about: {
      en: "The Panzer VIII Maus Land Cruiser Version set recreates a creative fusion of the Maus hull with a civilian SUV body. Over 3300 pieces build a unique model that blends military heritage with off-road styling.",
      pl: "Zestaw Panzer VIII Maus Land Cruiser Version odtwarza kreatywną fuzję kadłuba Mausa z nadwoziem SUV. Ponad 3300 elementów buduje unikalny model łączący militarną przeszłość z terenowym stylem.",
    },
    historicalBackground: {
      en: "A fictional or artistic interpretation combining the Maus super-heavy tank with Land Cruiser aesthetics. The real Maus was never completed as a production vehicle; this set offers a creative \"what if\" build.",
      pl: "Fikcyjna lub artystyczna interpretacja łącząca superciężki czołg Maus z estetyką Land Cruisera. Prawdziwy Maus nigdy nie wszedł do produkcji; ten zestaw to kreatywna propozycja „co by było, gdyby”.",
    },
  },
  {
    id: "95002",
    scale: "Other",
    numbering: "95002",
    name: "Trailer Set",
    recommendAge: "14+",
    pieces: 1728,
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "civilian",
    about: {
      en: "Trailer Set (car carrier): red flatbed transporter and sports car on the loading ramp.",
      pl: "Trailer Set to laweta: czerwona ciężarówka z rampą i auto sportowe na pokładzie.",
    },
  },
  {
    id: "97011",
    scale: "Other",
    numbering: "97011",
    name: "M26 Pershing",
    recommendAge: "10+",
    pieces: 2000,
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "tanks_vehicles",
    faction: "usa",
    about: {
      en: "The M26 Pershing set recreates the American heavy tank that arrived in Europe in 1945. Over 2000 pieces build a detailed model with the 90 mm M3 gun and sloped armor that matched the best German tanks.",
      pl: "Zestaw M26 Pershing odtwarza amerykański czołg ciężki, który dotarł do Europy w 1945 r. Ponad 2000 elementów buduje szczegółowy model z armatą 90 mm M3 i pochylonym pancerzem.",
    },
    historicalBackground: {
      en: "The M26 Pershing was America's first heavy tank. Its 90 mm gun could defeat Panthers and Tigers. Entered combat in February 1945. Evolved into the M46, M47, M48, and M60 Patton series.",
      pl: "M26 Pershing był pierwszym amerykańskim czołgiem ciężkim. Armata 90 mm niszczyła Pantery i Tygrysy. Wszedł do walki w lutym 1945 r. Ewoluował w serie Patton M46–M60.",
    },
  },
  {
    id: "98013",
    scale: "Other",
    numbering: "98013",
    name: "Type 076 Amphibious Assault Ship",
    recommendAge: "14+",
    pieces: 4525,
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "ships",
    about: {
      en: "The Type 076 Amphibious Assault Ship set recreates China's new amphibious assault ship concept. Over 4500 pieces build a large-scale model of this drone-capable vessel with a distinctive hull and flight deck.",
      pl: "Zestaw Type 076 Amphibious Assault Ship odtwarza chińską koncepcję okrętu desantowego. Ponad 4500 elementów buduje model w dużej skali tego zdolnego do obsługi dronów okrętu.",
    },
    historicalBackground: {
      en: "The Type 076 is a planned Chinese amphibious assault ship, featuring electromagnetic catapults for drones and possibly aircraft. It represents China's push for power projection capabilities.",
      pl: "Type 076 to planowany chiński okręt desantowy z elektromagnetycznymi katapultami dla dronów i prawdopodobnie samolotów. Oznacza rozwój chińskich zdolności projekcji siły.",
    },
  },
  {
    id: "98015",
    numbering: "98015",
    name: "U-BOOT U-96",
    recommendAge: "6+",
    pieces: 3565,
    scale: "1:72",
    productSize: "10.5 × 96.2 × 22.1 cm",
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "ships",
    faction: "germany",
    seoCategory: "ship",
    historicalBackground: {
      en: "U-96 was a Type VIIC U-boat commissioned in 1940. Under commanders such as Heinrich Lehmann-Willenbrock, she completed multiple war patrols in the Battle of the Atlantic. The boat became widely known through Lothar-Günther Buchheim's novel and the film Das Boot.",
      pl: "U-96 był okrętem podwodnym typu VIIC, przyjętym do służby w 1940 r. Pod dowództwem m.in. Heinricha Lehmann-Willenbrocka wykonał wiele patroli wojennych w bitwie o Atlantyk. Stał się znany dzięki powieści Lothara-Günthera Buchheima i filmowi Das Boot.",
    },
  },
  {
    id: "98016",
    numbering: "98016",
    name: "USS Enterprise CV-6",
    recommendAge: "14+",
    pieces: 4269,
    scale: "1:300",
    productSize: "82.7 × 9.8 × 26.4 cm",
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "ships",
    faction: "usa",
    seoCategory: "ship",
    historicalBackground: {
      en: "USS Enterprise (CV-6) was a Yorktown-class aircraft carrier of the United States Navy. Laid down in 1934 and commissioned in 1938, she became one of the most important Pacific Fleet carriers of World War II, earning 20 battle stars before decommissioning in 1947.",
      pl: "USS Enterprise (CV-6) był lotniskowcem typu Yorktown należącym do United States Navy. Stępkę położono w 1934 r., a do służby wszedł w 1938 r. Stał się jednym z najważniejszych lotniskowców Floty Pacyfiku podczas II wojny światowej; zdobył 20 gwiazd bojowych i został wycofany w 1947 r.",
    },
  },
  {
    id: "91013",
    numbering: "91013",
    name: "Lynx Tactical ATV",
    recommendAge: "14+",
    pieces: 737,
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "tanks_vehicles",
    seoCategory: "vehicle",
  },
  {
    id: "97009",
    scale: "1:28",
    numbering: "97009",
    name: "Tiger Heavy Tank",
    recommendAge: "10+",
    pieces: 2000,
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "tanks_vehicles",
    faction: "germany",
    about: {
      en: "The Tiger Heavy Tank set recreates the iconic German heavy tank. Over 2000 pieces build a detailed model of the Tiger I with its 8.8 cm KwK 36 gun and formidable armor that dominated battlefields from 1942.",
      pl: "Zestaw Tiger Heavy Tank odtwarza ikoniczny niemiecki czołg ciężki. Ponad 2000 elementów buduje szczegółowy model Tygrysa I z armatą 8.8 cm KwK 36 i potężnym pancerzem.",
    },
    historicalBackground: {
      en: "The Tiger I was Germany's most feared tank of WWII. Over 1300 were built. Its 88 mm gun and 100 mm frontal armor made it nearly invulnerable at long range. First deployed at Leningrad in 1942.",
      pl: "Tiger I był najbardziej budzącym respekt niemieckim czołgiem II wojny. Zbudowano ponad 1300 sztuk. Armata 88 mm i pancerz czołowy 100 mm czyniły go prawie nietykalnym na dystansie. Debiut pod Leningradem w 1942 r.",
    },
  },
  {
    id: "95001",
    scale: "Other",
    numbering: "95001",
    name: "Classic Off-Road Vehicle",
    recommendAge: "8+",
    pieces: 1200,
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "civilian",
    about: {
      en: "The Classic Off-Road Vehicle set recreates a rugged 4x4 for adventure. Over 1200 pieces build a detailed model with large tires, high ground clearance, and the styling of a timeless off-roader.",
      pl: "Zestaw Classic Off-Road Vehicle odtwarza terenowy 4x4 do przygód. Ponad 1200 elementów buduje szczegółowy model z dużymi oponami, wysokim prześwitem i ponadczasowym stylem.",
    },
  },
  {
    id: "97013",
    scale: "1:28",
    numbering: "97013",
    name: "Tiger Tank 007",
    recommendAge: "10+",
    pieces: 2000,
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "tanks_vehicles",
    faction: "germany",
    about: {
      en: "The Tiger Tank 007 set recreates another variant of the legendary Tiger I. Over 2000 pieces build a detailed model of this iconic tank, famous from films and historical accounts of tank warfare.",
      pl: "Zestaw Tiger Tank 007 odtwarza kolejną wersję legendarnego Tygrysa I. Ponad 2000 elementów buduje szczegółowy model ikonicznego czołgu znanego z filmów i relacji historycznych.",
    },
    historicalBackground: {
      en: "Tiger 007 was a common tactical number for Tiger tanks in German units. The Tiger I became a symbol of German armored might and a frequent subject of Allied intelligence and propaganda.",
      pl: "Tiger 007 to częsty numer taktyczny czołgów Tiger w jednostkach niemieckich. Tiger I stał się symbolem niemieckiej potęgi pancernej i częstym tematem wywiadu oraz propagandy aliantów.",
    },
  },
  {
    id: "98001",
    scale: "Other",
    numbering: "98001",
    name: "Panzer VIII Maus Heavy Tank",
    recommendAge: "10+",
    pieces: 2200,
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "tanks_vehicles",
    faction: "germany",
    about: {
      en: "The Panzer VIII Maus Heavy Tank set recreates the heaviest tank ever built. Over 2200 pieces build a detailed model of the 188-ton German super-heavy tank with its twin-gun turret.",
      pl: "Zestaw Panzer VIII Maus Heavy Tank odtwarza najcięższy czołg w historii. Ponad 2200 elementów buduje szczegółowy model 188-tonowego niemieckiego superciężkiego czołgu.",
    },
    historicalBackground: {
      en: "The Panzer VIII Maus weighed 188 tons. Two prototypes were built; neither saw combat. One complete example (with Krupp turret on Porsche hull) exists in Kubinka Tank Museum, Russia.",
      pl: "Panzer VIII Maus ważył 188 ton. Zbudowano dwa prototypy; żaden nie brał udziału w walce. Jeden kompletny egzemplarz znajduje się w muzeum w Kubince w Rosji.",
    },
  },
  {
    id: "97008",
    scale: "1:28",
    numbering: "97008",
    name: "Tiger Heavy Tank",
    recommendAge: "10+",
    pieces: 2000,
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "tanks_vehicles",
    faction: "germany",
    about: {
      en: "The Tiger Heavy Tank set recreates another variant of the legendary Tiger I. Over 2000 pieces build a detailed model with the 8.8 cm gun and heavy armor that made the Tiger a feared opponent.",
      pl: "Zestaw Tiger Heavy Tank odtwarza kolejną wersję legendarnego Tygrysa I. Ponad 2000 elementów buduje szczegółowy model z armatą 8.8 cm i ciężkim pancerzem.",
    },
    historicalBackground: {
      en: "Multiple Tiger I variants existed with minor differences. All shared the 88 mm KwK 36 and heavy armor. The Tiger's reputation often exceeded its actual numbers on the battlefield.",
      pl: "Istniało kilka wersji Tiger I z drobnymi różnicami. Wspólne były armata 88 mm KwK 36 i ciężki pancerz. Reputacja Tygrysa często przewyższała jego rzeczywistą liczebność.",
    },
  },
  {
    id: "96009",
    scale: "Other",
    numbering: "96009",
    name: "Unmanned Mechanical Dog",
    recommendAge: "10+",
    pieces: 1500,
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "civilian",
    about: {
      en: "The Unmanned Mechanical Dog set recreates a futuristic quadruped robot. Over 1500 pieces build a detailed model of an autonomous walking machine inspired by modern robotics and drone technology.",
      pl: "Zestaw Unmanned Mechanical Dog odtwarza futurystycznego czworonożnego robota. Ponad 1500 elementów buduje szczegółowy model autonomicznej maszyny kroczącej inspirowanej współczesną robotyką.",
    },
  },
  {
    id: "96010",
    scale: "Other",
    numbering: "96010",
    name: "625E Artillery Shell Integrated Air Defense Vehicle",
    recommendAge: "10+",
    pieces: 1600,
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "tanks_vehicles",
    faction: "china",
    about: {
      en: "The 625E Artillery Shell Integrated Air Defense Vehicle set recreates a modern Chinese self-propelled anti-aircraft system. Over 1600 pieces build a detailed model with integrated radar and missile/gun armament.",
      pl: "Zestaw 625E Artillery Shell Integrated Air Defense Vehicle odtwarza współczesny chiński samobieżny system przeciwlotniczy. Ponad 1600 elementów buduje szczegółowy model ze zintegrowanym radarem i uzbrojeniem.",
    },
    historicalBackground: {
      en: "The 625E is a Chinese short-range air defense system, typically mounted on a 6x6 chassis. It combines guns and/or missiles for engaging drones, helicopters, and low-flying aircraft.",
      pl: "625E to chiński krótkiego zasięgu system obrony powietrznej na podwoziu 6x6. Łączy działka i pociski do zwalczania dronów, śmigłowców i samolotów niskiego pułapu.",
    },
  },
  {
    id: "96008",
    scale: "Other",
    numbering: "96008",
    name: "ZBL-19 Infantry Vehicle",
    recommendAge: "10+",
    pieces: 1800,
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "tanks_vehicles",
    about: {
      en: "The ZBL-19 Infantry Vehicle set recreates the Chinese 8x8 wheeled infantry fighting vehicle. Over 1800 pieces build a detailed model with the remote weapon station and modular design.",
      pl: "Zestaw ZBL-19 Infantry Vehicle odtwarza chiński kołowy bojowy wóz piechoty 8x8. Ponad 1800 elementów buduje szczegółowy model ze zdalnie sterowaną wieżą i modułową konstrukcją.",
    },
    historicalBackground: {
      en: "The ZBL-08/09 family (export name VN1) is a Chinese 8x8 IFV in service since the 2000s. The ZBL-19 may refer to an upgraded or export variant with improved protection and firepower.",
      pl: "Rodzina ZBL-08/09 (eksport VN1) to chiński IFV 8x8 w służbie od lat 2000. ZBL-19 może oznaczać zmodernizowaną lub eksportową wersję z lepszą ochroną i siłą ognia.",
    },
  },
  {
    id: "98003",
    scale: "Other",
    numbering: "98003",
    name: "KV-2 Heavy Tank",
    recommendAge: "10+",
    pieces: 2100,
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "tanks_vehicles",
    faction: "ussr",
    about: {
      en: "The KV-2 Heavy Tank set recreates the Soviet assault tank with its massive turret. Over 2100 pieces build a detailed model with the distinctive 152 mm howitzer and tall turret.",
      pl: "Zestaw KV-2 Heavy Tank odtwarza sowiecki czołg szturmowy z masywną wieżą. Ponad 2100 elementów buduje szczegółowy model.",
    },
    historicalBackground: {
      en: "The KV-2 mounted a 152 mm howitzer in a tall turret. Its appearance and firepower shocked German forces in 1941. Mechanically unreliable, production ended in 1941 after roughly 330 units.",
      pl: "KV-2 miał haubicę 152 mm w wysokiej wieży. Jego wygląd i siła ognia szokowały Niemców w 1941 r. Z powodu awaryjności produkcję zakończono w 1941 r. po ok. 330 sztukach.",
    },
  },
  // Strona 3
  {
    id: "96002",
    scale: "Other",
    numbering: "96002",
    name: "Enhanced Main Battle Tank",
    recommendAge: "10+",
    pieces: 2500,
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "tanks_vehicles",
    about: {
      en: "The Enhanced Main Battle Tank set recreates a modern main battle tank concept. Over 2500 pieces build a detailed model with a contemporary turret, heavy armor, and smoothbore gun configuration.",
      pl: "Zestaw Enhanced Main Battle Tank odtwarza koncepcję współczesnego czołgu podstawowego. Ponad 2500 elementów buduje szczegółowy model z nowoczesną wieżą, ciężkim pancerzem i armatą gładkolufową.",
    },
    historicalBackground: {
      en: "Modern MBTs combine composite armor, 120–125 mm smoothbore guns, and advanced fire control. Designs like the Leopard 2, Abrams, and T-90 set the standard for 21st-century armor.",
      pl: "Współczesne czołgi podstawowe łączą pancerz kompozytowy, armaty gładkolufowe 120–125 mm i zaawansowane systemy kierowania ogniem. Leopard 2, Abrams i T-90 wyznaczają standardy.",
    },
  },
  {
    id: "97001",
    scale: "1:28",
    numbering: "97001",
    name: "Tiger Heavy Tank Snow",
    recommendAge: "10+",
    pieces: 2000,
    blockSize: "normal",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "tanks_vehicles",
    faction: "germany",
    about: {
      en: "The Tiger Heavy Tank Snow set recreates the Tiger I in winter camouflage. Over 2000 pieces build a detailed model of this iconic tank as it might have appeared on the Eastern Front in the snow.",
      pl: "Zestaw Tiger Heavy Tank Snow odtwarza Tygrysa I w zimowym kamuflażu. Ponad 2000 elementów buduje szczegółowy model ikonicznego czołgu we frontowej zimie.",
    },
    historicalBackground: {
      en: "Tiger tanks served extensively on the Eastern Front, where whitewash winter camouflage was commonly applied. The harsh Russian winter tested both men and machines during WWII.",
      pl: "Czołgi Tiger służyły masowo na froncie wschodnim, gdzie stosowano biały kamuflaż zimowy. Sroga rosyjska zima wystawiała na próbę ludzi i maszyny.",
    },
  },
  // Brixago line (German WWII armour)
  {
    id: "35001",
    brand: "Brixago",
    numbering: "35001",
    name: "Elefant Tank Destroyer",
    recommendAge: "6+",
    pieces: 1110,
    scale: "1:35",
    blockSize: "normal",
    productSize: "23.6 × 10.1 × 12.6 cm",
    packageSize: "42 × 25 × 9 cm",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "tanks_vehicles",
    faction: "germany",
    seoCategory: "tankDestroyer",
  },
  {
    id: "35002",
    brand: "Brixago",
    numbering: "35002",
    name: "Panther Tank",
    recommendAge: "6+",
    pieces: 953,
    scale: "1:35",
    blockSize: "normal",
    productSize: "24.6 × 11.2 × 12.4 cm",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "tanks_vehicles",
    faction: "germany",
    seoCategory: "tank",
  },
  {
    id: "35003",
    brand: "Brixago",
    numbering: "35003",
    name: "Tiger (P)",
    recommendAge: "6+",
    pieces: 1119,
    scale: "1:35",
    blockSize: "normal",
    productSize: "26.3 × 10 × 12.8 cm",
    material: "Eco-friendly ABS plastic",
    category: "military",
    militarySubcategory: "tanks_vehicles",
    faction: "germany",
    seoCategory: "tank",
  },
];

function applyProductCopy(list: ProductInput[]): Product[] {
  return list.map((product) => {
    const copy = PRODUCT_COPY[product.id];
    const withBrand: Product = {
      ...product,
      brand: product.brand ?? DEFAULT_PRODUCT_BRAND,
    };
    if (!copy) return withBrand;
    return {
      ...withBrand,
      about: copy.about,
      ...(copy.historicalBackground
        ? { historicalBackground: copy.historicalBackground }
        : {}),
    };
  });
}

export const products: Product[] = applyProductCopy(productsBase);

export const CATEGORIES: ProductCategory[] = ["military", "civilian"];
export const MILITARY_SUBCATEGORIES: MilitarySubcategory[] = [
  "tanks_vehicles",
  "ships",
  "other",
];
export const MILITARY_FACTIONS: MilitaryFaction[] = ["germany", "usa", "uk", "ussr", "china", "sweden"];

export function isMilitaryFaction(value: unknown): value is MilitaryFaction {
  return typeof value === "string" && MILITARY_FACTIONS.some((f) => f === value);
}

export type ProductSortOption = "default" | "number_asc" | "number_desc" | "pieces_asc" | "pieces_desc";

/** Domyślna kolejność zestawów */
const DEFAULT_PRODUCT_ORDER: string[] = [
  "35003", "35002", "35001",
  "95011", "97066", "95026", "95025", "95020", "97055", "91013", "98016", "95028", "95027", "97056", "98015", "95023", "95022", "97059", "95021", "97036", "97038", "97060", "95009", "95010", "95006", "95007", "97039", "97037", "97029", "97027", "97026", "97031", "97032", "97033", "97030", "98009", "97022", "97021", "97023", "95005", "95003", "98010", "95002",
  "97025", "97017", "97020", "97019", "97018", "98008", "98007", "97016",
  "96012", "96011", "98006", "99003", "99005", "97013", "95008", "95001", "98013", "97015",
  "97012", "98005", "98003", "99002", "96010", "99001", "97011", "97010",
  "97009", "97007", "97006", "97005", "98002", "98001", "97008", "97001", "97002", "97003",
  "96009", "96008", "96007", "96006", "96001", "96002", "96003", "96005",
];

/**
 * Strona główna (sekcja „najnowsze”): kolejność od ostatnio dodanego zestawu.
 * Przy nowym produkcie dopisz jego `id` na początku tablicy.
 */
const HOME_NEWEST_PRODUCT_IDS: string[] = ["95011", "97066", "35003", "35002", "35001", "95026", "95025", "95020", "97055", "91013", "98016", "95028", "95027", "97056", "98015", "95023", "95022", "97059", "95021", "97036", "97038", "97060", "95009", "95010", "95006"];

/** Ile zestawów pokazuje sekcja na stronie głównej. */
export const HOME_LATEST_PRODUCTS_LIMIT = 8;

/** Produkty na stronie głównej – najpierw lista „ostatnio dodane”, potem reszta domyślnej kolejności katalogu. */
export function getHomeLatestProducts(
  limit: number,
  brand: ProductBrand = DEFAULT_PRODUCT_BRAND,
): Product[] {
  const byId = new Map(products.map((p) => [p.id, p]));
  const result: Product[] = [];
  const seen = new Set<string>();
  const tailIds = DEFAULT_PRODUCT_ORDER.filter((id) => !HOME_NEWEST_PRODUCT_IDS.includes(id));
  const orderedIds = [...HOME_NEWEST_PRODUCT_IDS, ...tailIds];
  for (const id of orderedIds) {
    if (result.length >= limit) break;
    if (seen.has(id)) continue;
    const p = byId.get(id);
    if (p && p.brand === brand) {
      result.push(p);
      seen.add(id);
    }
  }
  if (result.length < limit) {
    for (const p of applySort(
      products.filter((x) => x.brand === brand && !seen.has(x.id)),
      "default",
    )) {
      if (result.length >= limit) break;
      result.push(p);
      seen.add(p.id);
    }
  }
  return result;
}

export interface ProductFilters {
  /** Search in name, numbering, material (case-insensitive) */
  searchQuery?: string;
  /** Manufacturer buckets (12GO / Brixago) – product matches any */
  brands?: ProductBrand[];
  category?: ProductCategory;
  militarySubcategory?: MilitarySubcategory;
  /** Multiple factions – product matches any */
  factions?: MilitaryFaction[];
  /** Single scale or comma-separated scales – product matches any */
  scales?: string[];
  piecesMin?: number;
  piecesMax?: number;
}

/** Min/max pieces from all products */
export function getPiecesRange(): { min: number; max: number } {
  let min = Infinity;
  let max = -Infinity;
  for (const p of products) {
    if (p.pieces < min) min = p.pieces;
    if (p.pieces > max) max = p.pieces;
  }
  return {
    min: Number.isFinite(min) ? min : 0,
    max: Number.isFinite(max) ? max : 5000,
  };
}

/** Unique scales from products (e.g. "1:35", "1:300") */
export function getUniqueScales(): string[] {
  const set = new Set<string>();
  for (const p of products) {
    if (p.scale) set.add(p.scale);
  }
  return Array.from(set).sort();
}

/** Compare product numberings for sort */
function compareNumbering(a: Product, b: Product, asc: boolean): number {
  const cmp = a.numbering.localeCompare(b.numbering, "en", { numeric: true });
  return asc ? cmp : -cmp;
}

function comparePieces(a: Product, b: Product, asc: boolean): number {
  const diff = a.pieces - b.pieces;
  return asc ? diff : -diff;
}

function brandSortKey(brand: ProductBrand): number {
  if (brand === "12GO") return 0;
  if (brand === "Brixago") return 1;
  return 2;
}

function applySort(items: Product[], sort: ProductSortOption): Product[] {
  const arr = items.slice();
  if (sort === "default") {
    const orderMap = new Map(DEFAULT_PRODUCT_ORDER.map((id, i) => [id, i]));
    arr.sort((a, b) => {
      const brandCmp = brandSortKey(a.brand) - brandSortKey(b.brand);
      if (brandCmp !== 0) return brandCmp;
      const ia = orderMap.has(a.id) ? orderMap.get(a.id)! : DEFAULT_PRODUCT_ORDER.length;
      const ib = orderMap.has(b.id) ? orderMap.get(b.id)! : DEFAULT_PRODUCT_ORDER.length;
      return ia - ib;
    });
  } else if (sort === "number_asc" || sort === "number_desc") {
    arr.sort((a, b) => compareNumbering(a, b, sort === "number_asc"));
  } else {
    arr.sort((a, b) => comparePieces(a, b, sort === "pieces_asc"));
  }
  return arr;
}

export function filterProducts(
  filters: ProductFilters,
  sort: ProductSortOption = "default"
): Product[] {
  const q = filters.searchQuery?.trim().toLowerCase();
  const filtered = products.filter((p) => {
    if (q) {
      const searchText = [
        p.name ?? "",
        p.numbering,
        p.id,
        p.brand,
        p.material,
        p.blockSize,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      if (!searchText.includes(q)) return false;
    }
    if (
      filters.brands &&
      filters.brands.length > 0 &&
      !filters.brands.includes(p.brand)
    ) {
      return false;
    }
    if (filters.category && p.category !== filters.category) return false;
    if (filters.militarySubcategory) {
      if (p.category !== "military" || p.militarySubcategory !== filters.militarySubcategory)
        return false;
    }
    if (
      filters.factions &&
      filters.factions.length > 0 &&
      (p.category !== "military" || !p.faction || !filters.factions.includes(p.faction))
    )
      return false;
    if (
      filters.scales &&
      filters.scales.length > 0 &&
      (!p.scale || !filters.scales.includes(p.scale))
    )
      return false;
    if (filters.piecesMin != null && p.pieces < filters.piecesMin) return false;
    if (filters.piecesMax != null && p.pieces > filters.piecesMax) return false;
    return true;
  });
  return applySort(filtered, sort);
}

/** All products in default order (for listings) */
export function getProductsSortedByNumbering(): Product[] {
  return applySort(products.slice(), "default");
}
